import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  FileText,
  Search,
  RefreshCw,
  Columns3,
  Check,
  ChevronDown,
  ChevronUp,
  CircleEllipsis,
  Plus,
  Edit2,
  Trash2,
  Copy,
  CalendarDays,
  Sliders,
  TableOfContents,
  AlertTriangle,
  Loader2,
  X,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Upload,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Download
} from 'lucide-react';
import {
  Modal,
  Box,
  Typography,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ScrollContainer from 'react-indiana-drag-scroll';
import type { ReportDocument, ReportGlobalSettings, ReportPage, WidgetItem, WidgetType } from './types';
import { Pagination } from '../InternalScan/InternalScanDashboard/Pagination';
import baseapi from '../../services/baseapi';
import { showSuccess, showError } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';
import { useTheme } from '../../context/ThemeContext';

const getModalTheme = (isDark: boolean) =>
  createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      background: {
        paper: isDark ? '#111827' : '#ffffff',
        default: isDark ? '#0b0f19' : '#f8fafc',
      },
      divider: isDark ? '#374151' : '#e2e8f0',
      primary: {
        main: '#f97316', // orange-500
      },
      text: {
        primary: isDark ? '#f3f4f6' : '#0f172a',
        secondary: isDark ? '#9ca3af' : '#64748b',
      }
    },
    typography: {
      fontSize: 12,
      fontFamily: 'inherit',
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: 12,
            backgroundColor: isDark ? '#1e293b' : '#f8fafc',
            borderRadius: 6,
          },
          input: {
            padding: '6px 10px',
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#374151' : '#cbd5e1',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#4b5563' : '#94a3b8',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f97316',
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: 12,
            color: isDark ? '#9ca3af' : '#64748b',
            '&.Mui-focused': {
              color: '#f97316',
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: '6px 10px',
            fontSize: 12,
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 12,
          }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            fontSize: 11,
            padding: '3px 8px',
            color: isDark ? '#9ca3af' : '#64748b',
            borderColor: isDark ? '#374151' : '#cbd5e1',
            textTransform: 'none',
            '&.Mui-selected': {
              backgroundColor: '#ea580c',
              color: '#ffffff',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#c2410c',
              }
            }
          }
        }
      }
    }
  });

type ColumnKey =
  | 'name'
  | 'description'
  | 'createdAt'
  | 'updatedAt'
  | 'export'
  | 'actions';

const DEFAULT_GLOBAL_SETTINGS: ReportGlobalSettings = {
  reportTitle: '',
  reportSubtitle: '',
  authorName: '',
  pageFormat: 'A4',
  orientation: 'portrait',
  pageMargin: 24,
  enableHeader: true,
  headerProperties: {
    content: '',
    headerSubtitle: '',
    image_url: '',
    logoAlign: 'left',
    logoHeight: 28,
    textAlign: 'left',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    dividerStyle: 'solid',
    fontSize: 13,
    fontWeight: 'bold',
    color: '#0f172a',
    skipOnFirstPage: false
  },
  enableFooter: true,
  footerProperties: {
    content: '',
    textAlign: 'left',
    showPagination: true,
    paginationFormat: 'page_of_total',
    paginationAlign: 'right',
    footerDateFormat: 'short',
    dateAlign: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    dividerStyle: 'solid',
    fontSize: 12,
    fontWeight: 'normal',
    color: '#64748b'
  }
};

export default function ReportsListTable() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const modalTheme = useMemo(() => getModalTheme(isDark), [isDark]);

  const [reports, setReports] = useState<ReportDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Column Visibility
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<ColumnKey, boolean>>({
    name: true,
    description: true,
    createdAt: true,
    updatedAt: true,
    export: true,
    actions: true
  });

  // Sorting
  const [sortConfig, setSortConfig] = useState<{ key: ColumnKey; direction: 'asc' | 'desc' } | null>({
    key: 'updatedAt',
    direction: 'desc'
  });

  // Action Menu Portal
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuDropdownRef = useRef<HTMLDivElement>(null);

  // Downloading State
  const [downloadingState, setDownloadingState] = useState<{ id: string; type: 'pdf' | 'docx' | 'excel' } | null>(null);

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<ReportDocument | null>(null);

  const [searchParams] = useSearchParams();
  const urlClientId = searchParams.get('client_id');
  const clientLevel = useClientLevel();
  const activeClientId = urlClientId
    ? (Number(urlClientId) || urlClientId)
    : (clientLevel?.id && clientLevel.name !== 'global'
      ? (Number(clientLevel.id) || clientLevel.id)
      : (clientLevel?.client_id
        ? (Number(clientLevel.client_id) || clientLevel.client_id)
        : (clientLevel?.id ? (Number(clientLevel.id) || clientLevel.id) : null)));

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState<1 | 2>(1);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [newReportMeta, setNewReportMeta] = useState({
    name: '',
    description: ''
  });
  const [newReportSettings, setNewReportSettings] = useState<ReportGlobalSettings>(DEFAULT_GLOBAL_SETTINGS);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // Fetch reports from API
  const fetchReports = async () => {
    setLoading(true);
    try {
      const queryObj: any = {
        ...(activeClientId ? { client_id: Number(activeClientId) || activeClientId } : {})
      };

      if (searchTerm) {
        queryObj.$or = [
          { report_name: { "$ilike": `%${searchTerm}%` } },
          { name: { "$ilike": `%${searchTerm}%` } }
        ];
      }

      const params: any = {
        query: JSON.stringify(queryObj)
      };

      const res = await baseapi.get('custom_reports', { params });
      const list = Array.isArray(res.data)
        ? res.data
        : (Array.isArray(res.data?.data)
          ? res.data.data
          : (Array.isArray(res.data?.reports) ? res.data.reports : []));

      const safeJsonParse = (val: any, fallback: any = {}) => {
        if (!val) return fallback;
        if (typeof val === 'object') return val;
        if (typeof val === 'string') {
          try {
            return JSON.parse(val);
          } catch {
            return fallback;
          }
        }
        return fallback;
      };

      const normalizedReports: ReportDocument[] = list.map((item: any, idx: number) => {
        const gs = item?.global_settings || item?.globalSettings || {};
        const extraSettings = safeJsonParse(gs?.extra_settings || gs?.extraSettings, {});
        const rawHeaderProps = safeJsonParse(gs?.header_properties || gs?.headerProperties, {});
        const rawFooterProps = safeJsonParse(gs?.footer_properties || gs?.footerProperties, {});
        const rawWidgets = Array.isArray(item?.widgets) ? item.widgets : [];
        let itemPages: ReportPage[] = [];

        if (Array.isArray(item?.pages) && item.pages.length > 0) {
          itemPages = item.pages.map((p: any) => ({
            ...p,
            widgets: Array.isArray(p.widgets) ? p.widgets.filter((w: any) => w && w.type !== 'header' && w.type !== 'footer' && w.widget_type !== 'header' && w.widget_type !== 'footer') : []
          }));
        } else if (rawWidgets.length > 0) {
          const pageMap = new Map<number, WidgetItem[]>();
          rawWidgets.forEach((w: any, wIdx: number) => {
            if (w.widget_type === 'header' || w.widget_type === 'footer' || w.type === 'header' || w.type === 'footer') {
              return;
            }
            const pageNum = Number(w.page_number) || 1;
            const pList = pageMap.get(pageNum) || [];
            const parsedProperties = safeJsonParse(w.properties, {});
            pList.push({
              id: String(w.id !== undefined ? w.id : `w-${wIdx}`),
              type: (w.widget_type || w.type || 'text') as WidgetType,
              x: Number.isFinite(Number(w.pos_x)) ? Number(w.pos_x) : (Number.isFinite(Number(w.x)) ? Number(w.x) : 0),
              y: Number.isFinite(Number(w.pos_y)) ? Number(w.pos_y) : (Number.isFinite(Number(w.y)) ? Number(w.y) : 0),
              w: Number.isFinite(Number(w.width)) ? Number(w.width) : (Number.isFinite(Number(w.w)) ? Number(w.w) : 6),
              h: Number.isFinite(Number(w.height)) ? Number(w.height) : (Number.isFinite(Number(w.h)) ? Number(w.h) : 4),
              properties: parsedProperties
            });
            pageMap.set(pageNum, pList);
          });
          const maxPageNum = Math.max(1, ...Array.from(pageMap.keys()));
          itemPages = Array.from({ length: maxPageNum }, (_, pIdx) => {
            const pNum = pIdx + 1;
            return {
              id: `page-${pNum}`,
              widgets: pageMap.get(pNum) || []
            };
          });
        }

        return {
          id: String(item?.id || item?._id || item?.report_id || `rep-${idx}`),
          name: item?.report_name || item?.name || item?.title || 'Untitled Report',
          description: item?.description || '',
          version: item?.version || '1.0.0',
          status: item?.status || 'draft',
          createdAt: item?.created || item?.createdAt || new Date().toISOString(),
          updatedAt: item?.updated || item?.updatedAt || new Date().toISOString(),
          author: item?.author || gs?.authorName || gs?.author_name || extraSettings?.authorName || '',
          globalSettings: {
            ...DEFAULT_GLOBAL_SETTINGS,
            reportTitle: gs.report_name || gs.reportTitle || item?.report_name || item?.name || '',
            reportSubtitle: gs.reportSubtitle || gs.report_subtitle || extraSettings?.reportSubtitle || rawHeaderProps?.headerSubtitle || rawHeaderProps?.header_subtitle || '',
            authorName: item?.author || gs.authorName || gs.author_name || extraSettings?.authorName || '',
            pageFormat: gs.page_format || gs.pageFormat || 'A4',
            pageMargin: gs.page_margin !== undefined ? Number(gs.page_margin) : 24,
            enableHeader: gs.enable_header !== undefined ? Boolean(gs.enable_header) : true,
            headerProperties: {
              ...DEFAULT_GLOBAL_SETTINGS.headerProperties,
              ...rawHeaderProps,
              headerSubtitle: rawHeaderProps?.headerSubtitle || rawHeaderProps?.header_subtitle || '',
              textAlign: rawHeaderProps?.textAlign || rawHeaderProps?.text_align || 'left',
              image_url: rawHeaderProps?.image_url || rawHeaderProps?.logoUrl || rawHeaderProps?.logo_url || '',
              logoAlign: rawHeaderProps?.logoAlign || rawHeaderProps?.logo_align || 'left'
            },
            enableFooter: gs.enable_footer !== undefined ? Boolean(gs.enable_footer) : true,
            footerProperties: {
              ...DEFAULT_GLOBAL_SETTINGS.footerProperties,
              ...rawFooterProps,
              textAlign: rawFooterProps?.textAlign || rawFooterProps?.text_align || 'left',
              paginationAlign: rawFooterProps?.paginationAlign || rawFooterProps?.pagination_align || 'right'
            }
          },
          pages: itemPages
        };
      });
      setReports(normalizedReports);
    } catch (err: any) {
      showError(err.response?.data?.message || err.message || 'Failed to fetch reports');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [activeClientId]);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target as Node)
      ) {
        setShowColumnSettings(false);
      }
      if (
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(event.target as Node)
      ) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Columns definition
  const columns: {
    key: ColumnKey;
    label: string;
    icon: React.ElementType;
    color: string;
  }[] = useMemo(() => [
    { key: 'name', label: 'Report Name', icon: FileText, color: 'text-orange-500 dark:text-orange-400' },
    { key: 'description', label: 'Description', icon: Sliders, color: 'text-indigo-500 dark:text-indigo-400' },
    { key: 'createdAt', label: 'Created', icon: CalendarDays, color: 'text-emerald-500 dark:text-emerald-400' },
    { key: 'updatedAt', label: 'Updated', icon: CalendarDays, color: 'text-blue-500 dark:text-blue-400' },
    { key: 'export', label: 'Export', icon: Download, color: 'text-purple-500 dark:text-purple-400' },
    { key: 'actions', label: 'Action', icon: TableOfContents, color: 'text-gray-700 dark:text-gray-100' }
  ], []);

  const toggleColumn = (key: ColumnKey) => {
    setColumnVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleAllColumns = () => {
    const allVisible = Object.values(columnVisibility).every(Boolean);
    const updated = Object.keys(columnVisibility).reduce((acc, k) => {
      acc[k as ColumnKey] = !allVisible;
      return acc;
    }, {} as Record<ColumnKey, boolean>);
    setColumnVisibility(updated);
  };

  const handleSort = (key: ColumnKey) => {
    setSortConfig(current => {
      if (!current || current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  // Filter & Sort Logic
  const filteredReports = useMemo(() => {
    return (reports || []).filter(r => {
      if (!r) return false;
      const matchesSearch =
        (r.name && r.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (r.description && r.description.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesSearch;
    });
  }, [reports, searchTerm]);

  const sortedReports = useMemo(() => {
    if (!sortConfig) return filteredReports;
    return [...filteredReports].sort((a, b) => {
      let aVal: any = a[sortConfig.key as keyof ReportDocument] || '';
      let bVal: any = b[sortConfig.key as keyof ReportDocument] || '';

      if (sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredReports, sortConfig]);

  // Pagination Logic
  const totalItems = sortedReports.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReports = sortedReports.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 2,
      left: rect.right - 160
    });
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const handleEditReport = (report: ReportDocument) => {
    setOpenMenuId(null);
    const safeId = report.id || (report as any)._id || 'rep-default';
    navigate(`/customreports/builder/${safeId}`);
  };

  const handleOpenCreateModal = () => {
    setCreateStep(1);
    setLogoFile(null);
    setNewReportMeta({
      name: '',
      description: ''
    });
    setNewReportSettings({
      ...DEFAULT_GLOBAL_SETTINGS,
      reportTitle: '',
      reportSubtitle: '',
      authorName: '',
      headerProperties: {
        ...DEFAULT_GLOBAL_SETTINGS.headerProperties,
        content: '',
        headerSubtitle: '',
        textAlign: 'left',
        image_url: ''
      },
      footerProperties: {
        ...DEFAULT_GLOBAL_SETTINGS.footerProperties,
        content: '',
        textAlign: 'left'
      }
    });
    setShowCreateModal(true);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setNewReportSettings(prev => ({
      ...prev,
      headerProperties: {
        ...prev.headerProperties,
        original_name: file.name,
        image_name: file.name,
        size: file.size,
        content_type: file.type,
        image_url: previewUrl
      }
    }));
  };

  const handleCreateSubmit = async () => {
    const reportName = newReportMeta.name.trim() || 'Untitled Report';
    const reportId = `rep-${Date.now()}`;

    const configuredSettings: ReportGlobalSettings = {
      ...newReportSettings,
      reportTitle: newReportSettings.reportTitle.trim() || reportName,
      reportSubtitle: '',
      authorName: newReportSettings.authorName || '',
      pageFormat: 'A4',
      headerProperties: {
        ...newReportSettings.headerProperties,
        content: newReportSettings.headerProperties.content !== undefined ? newReportSettings.headerProperties.content : '',
        headerSubtitle: newReportSettings.headerProperties?.headerSubtitle || ''
      }
    };

    try {
      const createPayload = {
        report_name: reportName,
        description: newReportMeta.description.trim() || null,
        client_id: activeClientId
      };

      const res = await baseapi.post('custom_reports', createPayload);
      const createdData = res.data?.data || res.data;
      const createdId = createdData?.id || (res.data as any)?._id || reportId;

      // Update global settings & upload logo if provided
      try {
        const cleanHeaderProps = { ...(configuredSettings.headerProperties || {}) };
        delete cleanHeaderProps.image_url;

        if (logoFile) {
          try {
            const formData = new FormData();
            formData.append('file', logoFile);
            const uploadRes = await baseapi.post(`custom_reports/${createdId}/upload-image`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            const uploadedData = uploadRes.data?.data || uploadRes.data;
            if (uploadedData) {
              cleanHeaderProps.image_name = uploadedData.image_name || logoFile.name;
              cleanHeaderProps.original_name = uploadedData.original_name || logoFile.name;
              cleanHeaderProps.image_key = uploadedData.image_key || '';
              cleanHeaderProps.size = uploadedData.size || logoFile.size;
              cleanHeaderProps.content_type = uploadedData.content_type || logoFile.type;
            }
          } catch (uploadErr) {
            console.error('Failed to upload logo during report creation:', uploadErr);
          }
        }

        await baseapi.put(`custom_reports/${createdId}/global-settings`, {
          page_format: configuredSettings.pageFormat || 'A4',
          page_margin: configuredSettings.pageMargin || 24,
          enable_header: configuredSettings.enableHeader,
          header_properties: cleanHeaderProps,
          enable_footer: configuredSettings.enableFooter,
          footer_properties: configuredSettings.footerProperties,
          extra_settings: {
            reportSubtitle: configuredSettings.reportSubtitle,
            authorName: configuredSettings.authorName
          }
        });
      } catch {
        // Fallback handled
      }

      setShowCreateModal(false);
      showSuccess('Report created! Opening builder...');
      navigate(`/customreports/builder/${createdId}`);
    } catch (err: any) {
      showError(err.response?.data?.message || err.message || 'Failed to create report on server');
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const queryObj: any = {
        ...(activeClientId ? { client_id: Number(activeClientId) || activeClientId } : {})
      };
      const res = await baseapi.post(`custom_reports/${id}/duplicate`, {}, {
        params: {
          query: JSON.stringify(queryObj)
        }
      });
      await fetchReports();
      setOpenMenuId(null);
      showSuccess(res.data?.message || 'Report duplicated successfully');
    } catch (err: any) {
      showError(err.response?.data?.message || err.message || 'Failed to duplicate report');
    }
  };

  const handleDeleteConfirm = async () => {
    if (reportToDelete) {
      const safeId = reportToDelete.id || (reportToDelete as any)._id;
      try {
        const queryObj: any = {
          ...(activeClientId ? { client_id: Number(activeClientId) || activeClientId } : {})
        };
        await baseapi.delete(`custom_reports/${safeId}`, {
          params: {
            query: JSON.stringify(queryObj)
          }
        });
        await fetchReports();
        setShowDeleteModal(false);
        setReportToDelete(null);
        showSuccess('Report deleted successfully');
      } catch (err: any) {
        showError(err.response?.data?.message || err.message || 'Failed to delete report');
      }
    }
  };

  const handleDownloadReport = async (id: string, reportName: string, type: 'pdf' | 'docx' | 'excel') => {
    setDownloadingState({ id, type });
    const extensions: Record<string, string> = { pdf: 'pdf', docx: 'docx', excel: 'xlsx' };
    const mimeTypes: Record<string, string> = {
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };

    try {
      let res;
      const queryObj: any = {
        ...(activeClientId ? { client_id: Number(activeClientId) || activeClientId } : {})
      };
      const downloadParams = {
        query: JSON.stringify(queryObj)
      };
      try {
        res = await baseapi.get(`custom_reports/${id}/${type}`, { responseType: 'blob', params: downloadParams });
      } catch (getErr: any) {
        if (getErr.response?.status === 405) {
          res = await baseapi.post(`custom_reports/${id}/${type}`, {}, { responseType: 'blob', params: downloadParams });
        } else {
          throw getErr;
        }
      }

      const blob = new Blob([res.data], { type: mimeTypes[type] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = res.headers?.['content-disposition'];
      let filename = `${reportName || 'custom_report'}_${id}.${extensions[type]}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      if (res.data?.message) {
        showSuccess(res.data.message);
      }
    } catch (err: any) {
      console.error(`Download ${type} error:`, err);
      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const parsed = JSON.parse(errorText);
          showError(parsed.message || parsed.detail || `Failed to download ${type.toUpperCase()}`);
        } catch {
          showError(`Failed to download ${type.toUpperCase()}`);
        }
      } else {
        showError(err.response?.data?.message || err.message || `Failed to download ${type.toUpperCase()}`);
      }
    } finally {
      setDownloadingState(null);
    }
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return iso;
    }
  };

  const renderCell = (key: ColumnKey, item: ReportDocument) => {
    const safeId = item.id || (item as any)._id || 'rep-id';

    switch (key) {
      case 'name':
        return (
          <td key={key} className="px-3 py-1.5 text-left">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 rounded bg-orange-500/10 border border-orange-500/20 text-orange-500 dark:text-orange-400 flex items-center justify-center flex-shrink-0">
                <FileText className="w-3 h-3" />
              </div>
              <div className="overflow-hidden min-w-0">
                <button
                  type="button"
                  onClick={() => handleEditReport(item)}
                  className="font-semibold text-gray-800 dark:text-gray-200 hover:text-orange-500 dark:hover:text-orange-400 transition-colors text-xs text-left truncate block max-w-xs cursor-pointer"
                >
                  {item.name}
                </button>
              </div>
            </div>
          </td>
        );

      case 'description':
        return (
          <td key={key} className="px-3 py-1.5 text-left text-[11px] text-gray-600 dark:text-gray-400 max-w-sm truncate">
            {item.description || '—'}
          </td>
        );

      case 'createdAt':
        return (
          <td key={key} className="px-3 py-1.5 text-center text-[10.5px] text-gray-600 dark:text-gray-300 font-mono">
            {formatDate(item.createdAt)}
          </td>
        );

      case 'updatedAt':
        return (
          <td key={key} className="px-3 py-1.5 text-center text-[10.5px] text-gray-600 dark:text-gray-300 font-mono">
            {formatDate(item.updatedAt)}
          </td>
        );

      case 'export': {
        const isPdfLoading = downloadingState?.id === safeId && downloadingState?.type === 'pdf';
        const isDocsLoading = downloadingState?.id === safeId && downloadingState?.type === 'docx';
        const isExcelLoading = downloadingState?.id === safeId && downloadingState?.type === 'excel';

        return (
          <td key={key} className="px-3 py-1.5 text-center">
            <div className="flex items-center justify-center space-x-2">
              {/* PDF Download Icon */}
              <button
                onClick={() => handleDownloadReport(safeId, item.name, 'pdf')}
                disabled={!!downloadingState}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors cursor-pointer disabled:opacity-50"
                title="Download PDF"
              >
                {isPdfLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-red-500 dark:text-red-400" />
                ) : (
                  <img src="/pdf.png" alt="PDF" className="w-4 h-4 object-contain" />
                )}
              </button>

              {/* DOCX Download Icon */}
              <button
                onClick={() => handleDownloadReport(safeId, item.name, 'docx')}
                disabled={!!downloadingState}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors cursor-pointer disabled:opacity-50"
                title="Download Word / DOCX"
              >
                {isDocsLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500 dark:text-blue-400" />
                ) : (
                  <img src="/docx.png" alt="DOCX" className="w-4 h-4 object-contain" />
                )}
              </button>

              {/* Excel Download Icon */}
              <button
                onClick={() => handleDownloadReport(safeId, item.name, 'excel')}
                disabled={!!downloadingState}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors cursor-pointer disabled:opacity-50"
                title="Download Excel"
              >
                {isExcelLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-500 dark:text-emerald-400" />
                ) : (
                  <img src="/xls.png" alt="Excel" className="w-4 h-4 object-contain" />
                )}
              </button>
            </div>
          </td>
        );
      }

      case 'actions':
        return (
          <td key={key} className="px-3 py-1.5 whitespace-nowrap text-center text-xs font-medium sticky right-0 z-10 bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800 transition-colors">
            <div className="flex items-center justify-center space-x-1 relative">
              {/* Options Menu Button */}
              <button
                onClick={(e) => handleMenuOpen(e, safeId)}
                className="text-gray-600 dark:text-white p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center cursor-pointer"
                title="More Options"
              >
                <CircleEllipsis className="w-4 h-4" />
              </button>

              {openMenuId === safeId &&
                createPortal(
                  <div
                    ref={menuDropdownRef}
                    className="mt-2 w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999] py-1 text-xs"
                    style={{
                      top: dropdownPosition.top,
                      left: dropdownPosition.left,
                      position: 'fixed'
                    }}
                  >
                    <button
                      onClick={() => handleEditReport(item)}
                      className="w-full text-left px-3 py-1.5 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2 font-medium cursor-pointer"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                      Edit in Builder
                    </button>
                    <button
                      onClick={() => handleDuplicate(safeId)}
                      className="w-full text-left px-3 py-1.5 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2 font-medium cursor-pointer"
                    >
                      <Copy className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                      Duplicate
                    </button>
                    <button
                      onClick={() => {
                        setReportToDelete(item);
                        setShowDeleteModal(true);
                        setOpenMenuId(null);
                      }}
                      className="w-full text-left px-3 py-1.5 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-gray-800 flex items-center gap-2 font-medium cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />
                      Delete
                    </button>
                  </div>,
                  document.body
                )}
            </div>
          </td>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 p-3 pb-6 min-h-screen text-gray-900 dark:text-gray-100 transition-colors">
      <div className="w-full mx-auto">
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-2.5">
          <div className="flex items-center gap-1.5">
            <div className="bg-orange-600 p-1.5 rounded shadow-lg">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-gray-900 dark:text-gray-200 leading-none">Custom Reports</h1>
            </div>
            {!loading && (
              <div className="font-bold text-base text-gray-700 dark:text-gray-100">
                ({totalItems})
              </div>
            )}
          </div>

          {/* Search & Action Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={fetchReports}
              disabled={loading}
              title="Refresh reports"
              className="h-8 w-8 flex items-center justify-center rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm transition-colors cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>

            <div className="relative w-full sm:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search reports..."
                className="w-full h-8 bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg pl-8 pr-3 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500 outline-none transition-all shadow-sm"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Column Toggle */}
            <div className="relative" ref={columnDropdownRef}>
              <button
                onClick={() => setShowColumnSettings(!showColumnSettings)}
                className="h-8 px-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Columns3 className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
              </button>

              {showColumnSettings && (
                <div className="absolute top-full mt-1.5 right-0 w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto">
                  <div className="px-3 py-1.5 text-[9px] font-bold text-gray-700 dark:text-gray-300 uppercase border-b border-gray-200 dark:border-gray-800 mb-1 flex items-center justify-between">
                    <span>Toggle Columns</span>
                    <button
                      onClick={toggleAllColumns}
                      className="text-[9px] text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 transition-colors cursor-pointer"
                    >
                      {Object.values(columnVisibility).every(Boolean) ? 'Hide All' : 'Show All'}
                    </button>
                  </div>
                  {columns.map(({ key, label, icon: Icon, color }) => (
                    <button
                      key={key}
                      onClick={() => toggleColumn(key)}
                      className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-bold uppercase text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-3 h-3 ${columnVisibility[key] ? color : 'text-gray-400 dark:text-gray-600'}`} />
                        {label}
                      </div>
                      <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${columnVisibility[key] ? 'bg-orange-600 border-orange-600' : 'border-gray-300 dark:border-gray-700'}`}>
                        {columnVisibility[key] && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Create Report Button */}
            <button
              onClick={handleOpenCreateModal}
              className="h-8 flex items-center gap-1.5 bg-orange-600 rounded-lg px-3 text-[11px] text-white font-bold hover:bg-orange-500 shadow-lg shadow-orange-900/20 transition-all cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              Create Report
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-t-xl overflow-hidden shadow-sm dark:shadow-2xl">
          <ScrollContainer
            className="overflow-x-auto custom-scroll cursor-grab active:cursor-grabbing"
            horizontal={true}
            vertical={false}
            hideScrollbars={false}
          >
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-gray-100 dark:bg-gray-800/80 sticky top-0 z-10">
                <tr className="border-b border-gray-200 dark:border-gray-700/50">
                  {columns.map(({ key, label }) =>
                    columnVisibility[key] ? (
                      <th
                        key={key}
                        onClick={() => key !== 'actions' && key !== 'export' && handleSort(key)}
                        className={`px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider select-none whitespace-nowrap ${key === 'actions'
                          ? 'text-center sticky right-0 bg-gray-100 dark:bg-gray-800 z-10'
                          : key === 'name' || key === 'description'
                            ? 'text-left cursor-pointer'
                            : key === 'export'
                              ? 'text-center'
                              : 'text-center cursor-pointer'
                          }`}
                      >
                        <div className={`flex items-center gap-1.5 ${key === 'name' || key === 'description' ? 'justify-start' : 'justify-center'}`}>
                          {/* <Icon className={`w-3 h-3 ${color}`} /> */}
                          {label}
                          {key !== 'actions' && key !== 'export' && (
                            sortConfig?.key === key ? (
                              sortConfig.direction === 'asc' ? (
                                <ChevronUp size={11} className="text-orange-500 dark:text-orange-400" />
                              ) : (
                                <ChevronDown size={11} className="text-orange-500 dark:text-orange-400" />
                              )
                            ) : (
                              <ChevronDown size={11} className="text-gray-400 dark:text-gray-500 opacity-60" />
                            )
                          )}
                        </div>
                      </th>
                    ) : null
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
                {paginatedReports.length === 0 && !loading ? (
                  <tr>
                    <td
                      colSpan={Object.values(columnVisibility).filter(Boolean).length}
                      className="py-10 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest italic"
                    >
                      <div className="flex flex-col items-center justify-center gap-1.5">
                        <FileText className="w-6 h-6 opacity-40 text-gray-400" />
                        <span className="text-[11px]">No reports found</span>
                      </div>
                    </td>
                  </tr>
                ) : loading ? (
                  <tr>
                    <td
                      colSpan={Object.values(columnVisibility).filter(Boolean).length}
                      className="py-10 text-center text-gray-700 dark:text-gray-200 font-bold uppercase tracking-widest italic"
                    >
                      <div className="flex flex-col items-center justify-center gap-1.5">
                        <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                        <span className="text-[11px]">Loading reports...</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedReports.map((item) => (
                    <tr
                      key={item.id || (item as any)._id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-200 dark:border-gray-700/30"
                    >
                      {columns.map(({ key }) =>
                        columnVisibility[key] ? renderCell(key, item) : null
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ScrollContainer>
        </div>

        {/* Pagination Footer */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>

      {/* Create Report Step-by-Step Modal */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <ThemeProvider theme={modalTheme}>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '650px', md: '700px' },
              maxWidth: '720px',
              maxHeight: '90vh',
              bgcolor: isDark ? '#111827' : '#ffffff',
              border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
              borderRadius: 2,
              boxShadow: 24,
              color: isDark ? '#f3f4f6' : '#111827',
              outline: 'none',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: 1,
                borderColor: isDark ? '#1f2937' : '#e5e7eb',
                bgcolor: isDark ? '#0f172a' : '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(249, 115, 22, 0.15)',
                    color: '#f97316',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {createStep === 1 ? <Sparkles size={16} /> : <Sliders size={16} />}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDark ? '#ffffff' : '#0f172a', lineHeight: 1.2 }}>
                    {createStep === 1 ? 'Create Report: Basic Details' : 'Configure Global Master Settings'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: isDark ? '#9ca3af' : '#64748b', mt: 0.25 }}>
                    {createStep === 1
                      ? 'Step 1 of 2 • Title, Description & Author'
                      : 'Step 2 of 2 • Page Layout, Master Header & Footer Defaults'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Step indicator badges */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    onClick={() => setCreateStep(1)}
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: 'pointer',
                      bgcolor: createStep === 1 ? '#f97316' : (isDark ? '#1e293b' : '#f1f5f9'),
                      color: createStep === 1 ? '#ffffff' : (isDark ? '#9ca3af' : '#64748b'),
                      border: '1px solid',
                      borderColor: createStep === 1 ? '#ea580c' : (isDark ? '#374151' : '#cbd5e1')
                    }}
                  >
                    1. Details
                  </Box>
                  <Typography sx={{ fontSize: 10, color: isDark ? '#6b7280' : '#94a3b8' }}>→</Typography>
                  <Box
                    onClick={() => {
                      if (newReportMeta.name.trim()) setCreateStep(2);
                    }}
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: newReportMeta.name.trim() ? 'pointer' : 'not-allowed',
                      bgcolor: createStep === 2 ? '#f97316' : (isDark ? '#1e293b' : '#f1f5f9'),
                      color: createStep === 2 ? '#ffffff' : (isDark ? '#9ca3af' : '#64748b'),
                      border: '1px solid',
                      borderColor: createStep === 2 ? '#ea580c' : (isDark ? '#374151' : '#cbd5e1')
                    }}
                  >
                    2. Global Settings
                  </Box>
                </Box>

                <Button
                  onClick={() => setShowCreateModal(false)}
                  size="small"
                  sx={{
                    minWidth: 28,
                    width: 28,
                    height: 28,
                    p: 0,
                    color: isDark ? '#9ca3af' : '#64748b',
                    '&:hover': { bgcolor: isDark ? '#1e293b' : '#f1f5f9', color: isDark ? '#ffffff' : '#0f172a' }
                  }}
                >
                  <X size={16} />
                </Button>
              </Box>
            </Box>

            {/* Modal Body */}
            <Box
              sx={{
                p: 2.5,
                overflowY: 'auto',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              {createStep === 1 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Report Identification
                    </Typography>
                    <TextField
                      label="Report Title"
                      placeholder="e.g. Executive Vulnerability Assessment Q3"
                      value={newReportMeta.name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewReportMeta(prev => ({ ...prev, name: val }));
                        setNewReportSettings(prev => ({
                          ...prev,
                          reportTitle: val
                        }));
                      }}
                      required
                      fullWidth
                      size="small"
                      autoFocus
                    />
                  </Box>

                  <Divider sx={{ borderColor: isDark ? '#1f2937' : '#e5e7eb' }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Summary & Description
                    </Typography>
                    <TextField
                      label="Description"
                      placeholder="Brief description of the report purpose, scope, and audience..."
                      value={newReportMeta.description}
                      onChange={(e) => setNewReportMeta(prev => ({ ...prev, description: e.target.value }))}
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* 1. Page Geometry & Layout */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Page & Grid
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography sx={{ fontSize: 11, color: isDark ? '#9ca3af' : '#64748b' }}>Page Margins</Typography>
                      <ToggleButtonGroup
                        value={newReportSettings.pageMargin !== undefined ? newReportSettings.pageMargin : 24}
                        exclusive
                        onChange={(_, val) => val !== null && setNewReportSettings(prev => ({ ...prev, pageMargin: Number(val) }))}
                        size="small"
                        fullWidth
                      >
                        <ToggleButton value={16} sx={{ fontSize: 11, py: 0.4 }}>16px</ToggleButton>
                        <ToggleButton value={24} sx={{ fontSize: 11, py: 0.4 }}>24px</ToggleButton>
                        <ToggleButton value={32} sx={{ fontSize: 11, py: 0.4 }}>32px</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: isDark ? '#1f2937' : '#e5e7eb' }} />

                  {/* 2. Master Header Configuration */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Master Header
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={newReportSettings.enableHeader}
                            onChange={(e) => setNewReportSettings(prev => ({ ...prev, enableHeader: e.target.checked }))}
                            size="small"
                            color="primary"
                          />
                        }
                        label={<Typography sx={{ fontSize: 11, color: isDark ? '#cbd5e1' : '#334155' }}>Enable Header</Typography>}
                        sx={{ m: 0 }}
                      />
                    </Box>

                    {newReportSettings.enableHeader && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, bgcolor: isDark ? 'rgba(30, 41, 59, 0.4)' : '#f8fafc', borderRadius: 1.5, border: isDark ? '1px solid #374151' : '1px solid #e2e8f0' }}>
                        {/* Logo Section */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.25, bgcolor: isDark ? '#1e293b' : '#ffffff', borderRadius: 1.5, border: isDark ? '1px solid #334155' : '1px solid #e2e8f0' }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f97316', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Header Logo
                          </Typography>

                          {/* Current Logo Preview & Metadata */}
                          {(newReportSettings.headerProperties?.image_url || newReportSettings.headerProperties?.image_key) && (
                            <Box sx={{ p: 1, bgcolor: isDark ? '#0f172a' : '#f8fafc', borderRadius: 1, border: isDark ? '1px solid #334155' : '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              <Box sx={{ width: '100%', height: 60, bgcolor: isDark ? '#1e293b' : '#ffffff', borderRadius: 0.75, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <img
                                  src={newReportSettings.headerProperties?.image_url || (newReportSettings.headerProperties?.image_key ? `/api/${newReportSettings.headerProperties.image_key}` : '')}
                                  alt={newReportSettings.headerProperties?.original_name || 'Logo Preview'}
                                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                                  {newReportSettings.headerProperties?.original_name || newReportSettings.headerProperties?.image_name || 'Header Logo'}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setLogoFile(null);
                                    setNewReportSettings(prev => ({
                                      ...prev,
                                      headerProperties: {
                                        ...prev.headerProperties,
                                        image_url: '',
                                        image_key: '',
                                        image_name: '',
                                        original_name: ''
                                      }
                                    }));
                                  }}
                                  sx={{ color: '#ef4444', p: 0.25 }}
                                  title="Remove Logo"
                                >
                                  <Trash2 size={13} />
                                </IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                                {newReportSettings.headerProperties?.size && (
                                  <Typography sx={{ fontSize: 9.5, color: isDark ? '#94a3b8' : '#64748b' }}>
                                    {(newReportSettings.headerProperties.size / 1024).toFixed(1)} KB
                                  </Typography>
                                )}
                                {newReportSettings.headerProperties?.content_type && (
                                  <Typography sx={{ fontSize: 9.5, color: '#0284c7', bgcolor: 'rgba(2, 132, 199, 0.1)', px: 0.5, py: 0.1, borderRadius: 0.5 }}>
                                    {newReportSettings.headerProperties.content_type}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          {/* Upload Button */}
                          <Button
                            variant="contained"
                            size="small"
                            component="label"
                            startIcon={<Upload size={13} />}
                            sx={{
                              fontSize: 11,
                              textTransform: 'none',
                              bgcolor: '#ea580c',
                              '&:hover': { bgcolor: '#c2410c' },
                              py: 0.6
                            }}
                          >
                            {newReportSettings.headerProperties?.image_url ? 'Replace Logo' : 'Upload Logo File'}
                            <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                          </Button>

                          {/* Logo Alignment & Height */}
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Logo Align</InputLabel>
                              <Select
                                value={newReportSettings.headerProperties?.logoAlign || 'left'}
                                label="Logo Align"
                                onChange={(e) => setNewReportSettings(prev => ({
                                  ...prev,
                                  headerProperties: { ...prev.headerProperties, logoAlign: e.target.value as any }
                                }))}
                              >
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="center">Center</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                                <MenuItem value="none">Hidden</MenuItem>
                              </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                              <InputLabel>Height</InputLabel>
                              <Select
                                value={newReportSettings.headerProperties?.logoHeight || 28}
                                label="Height"
                                onChange={(e) => setNewReportSettings(prev => ({
                                  ...prev,
                                  headerProperties: { ...prev.headerProperties, logoHeight: Number(e.target.value) }
                                }))}
                              >
                                <MenuItem value={20}>20px</MenuItem>
                                <MenuItem value={28}>28px</MenuItem>
                                <MenuItem value={36}>36px</MenuItem>
                                <MenuItem value={48}>48px</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>

                        {/* Header Title & Subtitle in 2 Columns */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                          <TextField
                            label="Report Header Title"
                            value={newReportSettings.headerProperties?.content || ''}
                            placeholder="e.g. Header Title"
                            onChange={(e) => setNewReportSettings(prev => ({
                              ...prev,
                              headerProperties: { ...prev.headerProperties, content: e.target.value }
                            }))}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Report Subtitle"
                            value={newReportSettings.headerProperties?.headerSubtitle || ''}
                            placeholder="e.g. Confidential Assessment"
                            onChange={(e) => setNewReportSettings(prev => ({
                              ...prev,
                              headerProperties: { ...prev.headerProperties, headerSubtitle: e.target.value }
                            }))}
                            fullWidth
                            size="small"
                          />
                        </Box>

                        {/* Text Alignment & Divider Style */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 10, color: isDark ? '#9ca3af' : '#64748b' }}>Text Alignment</Typography>
                            <ToggleButtonGroup
                              value={newReportSettings.headerProperties?.textAlign || 'right'}
                              exclusive
                              onChange={(_, val) => val && setNewReportSettings(prev => ({
                                ...prev,
                                headerProperties: { ...prev.headerProperties, textAlign: val }
                              }))}
                              size="small"
                              fullWidth
                            >
                              <ToggleButton value="left" sx={{ py: 0.25 }}><AlignLeft size={13} /></ToggleButton>
                              <ToggleButton value="center" sx={{ py: 0.25 }}><AlignCenter size={13} /></ToggleButton>
                              <ToggleButton value="right" sx={{ py: 0.25 }}><AlignRight size={13} /></ToggleButton>
                            </ToggleButtonGroup>
                          </Box>

                          <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontSize: 12 }}>Header Divider Style</InputLabel>
                            <Select
                              value={newReportSettings.headerProperties?.dividerStyle || 'solid'}
                              label="Header Divider Style"
                              onChange={(e) => setNewReportSettings(prev => ({
                                ...prev,
                                headerProperties: { ...prev.headerProperties, dividerStyle: e.target.value as any }
                              }))}
                              sx={{ fontSize: 12 }}
                            >
                              <MenuItem value="solid" sx={{ fontSize: 12 }}>Solid Line</MenuItem>
                              <MenuItem value="dashed" sx={{ fontSize: 12 }}>Dashed</MenuItem>
                              <MenuItem value="dotted" sx={{ fontSize: 12 }}>Dotted</MenuItem>
                              <MenuItem value="none" sx={{ fontSize: 12 }}>None</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={newReportSettings.headerProperties?.skipOnFirstPage || false}
                              onChange={(e) => setNewReportSettings(prev => ({
                                ...prev,
                                headerProperties: { ...prev.headerProperties, skipOnFirstPage: e.target.checked }
                              }))}
                            />
                          }
                          label={<Typography sx={{ fontSize: 11, color: isDark ? '#cbd5e1' : '#334155' }}>Skip on Page 1 (Cover Page)</Typography>}
                        />
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ borderColor: isDark ? '#1f2937' : '#e5e7eb' }} />

                  {/* 3. Master Footer Configuration */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Master Footer
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={newReportSettings.enableFooter}
                            onChange={(e) => setNewReportSettings(prev => ({ ...prev, enableFooter: e.target.checked }))}
                            size="small"
                            color="primary"
                          />
                        }
                        label={<Typography sx={{ fontSize: 11, color: isDark ? '#cbd5e1' : '#334155' }}>Enable Footer</Typography>}
                        sx={{ m: 0 }}
                      />
                    </Box>

                    {newReportSettings.enableFooter && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, bgcolor: isDark ? 'rgba(30, 41, 59, 0.4)' : '#f8fafc', borderRadius: 1.5, border: isDark ? '1px solid #374151' : '1px solid #e2e8f0' }}>
                        <TextField
                          label="Footer Label / Security Notice"
                          placeholder="e.g. Confidential • Internal Use Only"
                          value={newReportSettings.footerProperties?.content || ''}
                          onChange={(e) => setNewReportSettings(prev => ({
                            ...prev,
                            footerProperties: { ...prev.footerProperties, content: e.target.value }
                          }))}
                          fullWidth
                          size="small"
                        />

                        {/* Footer Text Alignment */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography sx={{ fontSize: 10, color: isDark ? '#9ca3af' : '#64748b', minWidth: 120 }}>Footer Text Alignment</Typography>
                          <ToggleButtonGroup
                            value={newReportSettings.footerProperties?.textAlign || 'left'}
                            exclusive
                            onChange={(_, val) => val && setNewReportSettings(prev => ({
                              ...prev,
                              footerProperties: { ...prev.footerProperties, textAlign: val }
                            }))}
                            size="small"
                            fullWidth
                          >
                            <ToggleButton value="left" sx={{ fontSize: 10, py: 0.25 }}><AlignLeft size={13} /></ToggleButton>
                            <ToggleButton value="center" sx={{ fontSize: 10, py: 0.25 }}><AlignCenter size={13} /></ToggleButton>
                            <ToggleButton value="right" sx={{ fontSize: 10, py: 0.25 }}><AlignRight size={13} /></ToggleButton>
                          </ToggleButtonGroup>
                        </Box>

                        {/* Pagination Format & Placement */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'center' }}>
                          <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontSize: 12 }}>Pagination Format</InputLabel>
                            <Select
                              value={newReportSettings.footerProperties?.paginationFormat || 'page_of_total'}
                              label="Pagination Format"
                              onChange={(e) => setNewReportSettings(prev => ({
                                ...prev,
                                footerProperties: { ...prev.footerProperties, paginationFormat: e.target.value as any }
                              }))}
                              sx={{ fontSize: 12 }}
                            >
                              <MenuItem value="page_of_total" sx={{ fontSize: 12 }}>Page 1 of 5</MenuItem>
                              <MenuItem value="slash" sx={{ fontSize: 12 }}>1 / 5</MenuItem>
                              <MenuItem value="page_only" sx={{ fontSize: 12 }}>Page 1</MenuItem>
                              <MenuItem value="number_only" sx={{ fontSize: 12 }}>1</MenuItem>
                            </Select>
                          </FormControl>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 10, color: isDark ? '#9ca3af' : '#64748b' }}>Pagination Placement</Typography>
                            <ToggleButtonGroup
                              value={newReportSettings.footerProperties?.paginationAlign || 'right'}
                              exclusive
                              onChange={(_, val) => val && setNewReportSettings(prev => ({
                                ...prev,
                                footerProperties: { ...prev.footerProperties, paginationAlign: val }
                              }))}
                              size="small"
                              fullWidth
                            >
                              <ToggleButton value="left" sx={{ fontSize: 10, py: 0.25 }}>Left</ToggleButton>
                              <ToggleButton value="center" sx={{ fontSize: 10, py: 0.25 }}>Center</ToggleButton>
                              <ToggleButton value="right" sx={{ fontSize: 10, py: 0.25 }}>Right</ToggleButton>
                              <ToggleButton value="none" sx={{ fontSize: 10, py: 0.25 }}>Off</ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                        </Box>

                        {/* Footer Divider Style */}
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ fontSize: 12 }}>Footer Divider Style</InputLabel>
                          <Select
                            value={newReportSettings.footerProperties?.dividerStyle || 'solid'}
                            label="Footer Divider Style"
                            onChange={(e) => setNewReportSettings(prev => ({
                              ...prev,
                              footerProperties: { ...prev.footerProperties, dividerStyle: e.target.value as any }
                            }))}
                            sx={{ fontSize: 12 }}
                          >
                            <MenuItem value="solid" sx={{ fontSize: 12 }}>Solid Line</MenuItem>
                            <MenuItem value="dashed" sx={{ fontSize: 12 }}>Dashed</MenuItem>
                            <MenuItem value="dotted" sx={{ fontSize: 12 }}>Dotted</MenuItem>
                            <MenuItem value="none" sx={{ fontSize: 12 }}>None</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Modal Actions */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: 1,
                borderColor: isDark ? '#1f2937' : '#e5e7eb',
                bgcolor: isDark ? '#0f172a' : '#f8fafc',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}
            >
              {createStep === 2 ? (
                <Button
                  onClick={() => setCreateStep(1)}
                  startIcon={<ArrowLeft size={13} />}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'none',
                    color: isDark ? '#d1d5db' : '#475569',
                    '&:hover': { bgcolor: isDark ? '#1e293b' : '#e2e8f0', color: isDark ? '#ffffff' : '#0f172a' }
                  }}
                >
                  Back
                </Button>
              ) : (
                <Box />
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                  onClick={() => setShowCreateModal(false)}
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    textTransform: 'none',
                    color: isDark ? '#9ca3af' : '#64748b',
                    '&:hover': { bgcolor: isDark ? '#1e293b' : '#e2e8f0', color: isDark ? '#ffffff' : '#0f172a' }
                  }}
                >
                  Cancel
                </Button>

                {createStep === 1 ? (
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (!newReportMeta.name.trim()) {
                        showError('Please enter a report title');
                        return;
                      }
                      setCreateStep(2);
                    }}
                    endIcon={<ArrowRight size={13} />}
                    sx={{
                      bgcolor: '#f97316',
                      '&:hover': { bgcolor: '#ea580c' },
                      fontSize: 11,
                      fontWeight: 700,
                      color: "white",
                      textTransform: 'none',
                      px: 2,
                      py: 0.6,
                      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                    }}
                  >
                    Next: Global Settings
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleCreateSubmit}
                    endIcon={<ArrowRight size={13} />}
                    sx={{
                      bgcolor: '#10b981',
                      '&:hover': { bgcolor: '#059669' },
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'none',
                      px: 2,
                      py: 0.6,
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                    }}
                  >
                    Create & Open Builder
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </ThemeProvider>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90%] sm:w-[440px] max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl p-5 rounded-xl text-gray-900 dark:text-white outline-none">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Delete Custom Report?</h2>
          </div>

          <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed mb-5">
            Are you sure you want to delete <strong className="text-gray-900 dark:text-white">"{reportToDelete?.name}"</strong>? This will permanently remove the report pages, widgets, and layout configuration.
          </p>

          <div className="flex justify-end items-center gap-2">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="px-4 py-1.5 text-xs font-bold bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer shadow-lg shadow-red-900/30"
            >
              Delete Report
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
